import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatCLP, dateLabel, dateTimeLabel, statusBadge, ACCREDITATION_META } from "../../lib/format";
import { Users, Truck, Package, CurrencyDollar, CircleNotch, Eye, CheckCircle, XCircle, PauseCircle, ChartBar, MapTrifold, Envelope, Trash } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { CarriersMap } from "../../components/CarriersMap";

export default function AdminDashboard() {
  const [searchParams] = useSearchParams();
  const [metrics, setMetrics] = useState(null);
  const [tab, setTab] = useState(searchParams.get("tab") || "panel");
  const [carriers, setCarriers] = useState([]);
  const [freights, setFreights] = useState([]);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadMetrics = () => api.get("/admin/metrics").then((r) => setMetrics(r.data)).catch(() => {});
  const loadCarriers = () => api.get("/admin/carriers").then((r) => setCarriers(r.data)).catch(() => {});
  const loadFreights = () => api.get("/admin/freights").then((r) => setFreights(r.data)).catch(() => {});
  const loadLocations = () => api.get("/carriers/locations").then((r) => setLocations(r.data)).catch(() => {});
  const loadUsers = () => api.get("/admin/users").then((r) => setUsers(r.data.filter((u) => u.role !== "SYSTEM"))).catch(() => {});
  const loadMessages = () => api.get("/admin/contact-messages").then((r) => setMessages(r.data)).catch(() => {});

  useEffect(() => { loadMetrics(); loadCarriers(); loadFreights(); loadLocations(); loadUsers(); loadMessages(); }, []);

  useEffect(() => {
    const t = searchParams.get("tab") || "panel";
    setTab(t);
    if (t === "map") loadLocations();
    if (t === "users") loadUsers();
    if (t === "messages") loadMessages();
  }, [searchParams]);

  const markRead = async (id, read) => {
    try { await api.patch(`/admin/contact-messages/${id}`, { read }); loadMessages(); }
    catch (e) { toast.error("Error"); }
  };
  const deleteMsg = async (id) => {
    if (!window.confirm("¿Eliminar este mensaje?")) return;
    try { await api.delete(`/admin/contact-messages/${id}`); toast.success("Mensaje eliminado"); loadMessages(); }
    catch (e) { toast.error("Error"); }
  };

  const updateUser = async (id, patch) => {
    try {
      await api.patch(`/admin/users/${id}`, patch);
      toast.success("Usuario actualizado");
      loadUsers(); loadCarriers(); loadMetrics();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const PER_PAGE = 10;
  const [cPage, setCPage] = useState(1);
  const [uPage, setUPage] = useState(1);
  const [fPage, setFPage] = useState(1);
  const [mPage, setMPage] = useState(1);
  useEffect(() => { setCPage(1); setUPage(1); setFPage(1); setMPage(1); }, [tab]);
  const paginate = (arr, page) => arr.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const SECTION_TITLES = { panel: "Resumen", carriers: "Transportistas", users: "Usuarios", freights: "Fletes", messages: "Mensajes", map: "Mapa en vivo" };

  const openDetail = async (userId) => {
    const { data } = await api.get(`/admin/carriers/${userId}/detail`);
    setDetail(data);
  };

  const setStatus = async (userId, status) => {
    setBusy(true);
    try {
      await api.patch(`/admin/carriers/${userId}/accreditation`, { status });
      toast.success(`Acreditación: ${status}`);
      loadCarriers(); loadMetrics();
      if (detail) openDetail(userId);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  const reviewDoc = async (docId, status) => {
    let reason = "";
    if (status === "RECHAZADO") { reason = window.prompt("Motivo del rechazo:") || ""; }
    try {
      await api.patch(`/admin/documents/${docId}/review`, { status, reason });
      toast.success("Documento actualizado");
      if (detail) openDetail(detail.user.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-dashboard">
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A36]">Panel administrador</span>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1 mb-2">{SECTION_TITLES[tab] || "Marketplace Frets"}</h1>
      <p className="text-sm text-zinc-500 mb-6">Marketplace Frets · usa el menú lateral para navegar</p>

      {tab === "panel" && (
        !metrics ? (
          <div className="flex justify-center py-16"><CircleNotch size={36} className="animate-spin text-[#FF5A36]" /></div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="admin-metrics">
            <Metric icon={Users} label="Clientes" value={metrics.clients} />
            <Metric icon={Truck} label="Transportistas" value={metrics.carriers} />
            <Metric icon={PauseCircle} label="Pend. acreditación" value={metrics.pendingAccreditations} accent />
            <Metric icon={CheckCircle} label="Aprobados" value={metrics.approvedCarriers} />
            <Metric icon={Package} label="Fletes publicados" value={metrics.freightsPublished} />
            <Metric icon={Package} label="Adjudicados" value={metrics.freightsAwarded} />
            <Metric icon={CheckCircle} label="Completados" value={metrics.freightsCompleted} />
            <Metric icon={XCircle} label="Cancelados" value={metrics.freightsCancelled} />
            <Metric icon={CurrencyDollar} label="Valor total (GMV)" value={formatCLP(metrics.totalValue)} />
            <Metric icon={CurrencyDollar} label="Ticket promedio" value={formatCLP(metrics.averageTicket)} />
            <Metric icon={ChartBar} label="Ofertas / solicitud" value={metrics.averageOffersPerRequest} />
          </div>
        )
      )}

      {tab === "users" && (
        <div className="overflow-x-auto rounded-2xl border border-zinc-200 bg-white" data-testid="users-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-left text-xs uppercase tracking-widest text-zinc-500">
              <tr><Th>Nombre</Th><Th>Email</Th><Th>Ciudad</Th><Th>Rol</Th><Th>Estado</Th><Th>Acción</Th></tr>
            </thead>
            <tbody>
              {paginate(users, uPage).map((u) => (
                <tr key={u.id} className="border-t border-zinc-100" data-testid={`user-row-${u.id}`}>
                  <Td className="font-medium">{u.name} {u.lastName}</Td>
                  <Td className="text-zinc-500">{u.email}</Td>
                  <Td>{u.city || "—"}</Td>
                  <Td>
                    <select value={u.role} data-testid={`user-role-${u.id}`} onChange={(e) => updateUser(u.id, { role: e.target.value })}
                      className="h-9 px-2 rounded-lg border border-zinc-300 bg-white text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-[#FF5A36]">
                      <option value="CLIENT">Cliente</option>
                      <option value="CARRIER">Transportista</option>
                      <option value="ADMIN">Administrador</option>
                    </select>
                  </Td>
                  <Td>
                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${u.status === "SUSPENDED" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {u.status === "SUSPENDED" ? "Suspendido" : "Activo"}
                    </span>
                  </Td>
                  <Td>
                    <button onClick={() => updateUser(u.id, { status: u.status === "SUSPENDED" ? "ACTIVE" : "SUSPENDED" })} data-testid={`toggle-user-${u.id}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold text-white ${u.status === "SUSPENDED" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-zinc-700 hover:bg-zinc-800"}`}>
                      {u.status === "SUSPENDED" ? "Activar" : "Suspender"}
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "map" && (
        <div data-testid="admin-map">
          <p className="text-sm text-zinc-500 mb-3 flex items-center gap-2"><MapTrifold size={18} className="text-[#FF5A36]" /> Ubicación de transportistas acreditados ({locations.filter(c => c.online).length} en línea de {locations.length})</p>
          <CarriersMap carriers={locations} height="calc(100vh - 320px)" />
        </div>
      )}

      {tab === "carriers" && (
        <div className="overflow-x-auto rounded-2xl border border-zinc-200 bg-white" data-testid="carriers-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-left text-xs uppercase tracking-widest text-zinc-500">
              <tr>
                <Th>Empresa / Contacto</Th><Th>Ciudad</Th><Th>Vehículo</Th><Th>Estado</Th><Th>Servicios</Th><Th>Rating</Th><Th>Acción</Th>
              </tr>
            </thead>
            <tbody>
              {paginate(carriers, cPage).map((c) => {
                const meta = ACCREDITATION_META[c.accreditationStatus] || {};
                return (
                  <tr key={c.userId} className="border-t border-zinc-100" data-testid={`carrier-row-${c.userId}`}>
                    <Td className="font-medium">
                      {c.companyName
                        ? <><div>{c.companyName}</div><div className="text-xs font-normal text-zinc-500">{c.name}</div></>
                        : c.name}
                    </Td>
                    <Td>{c.city}</Td>
                    <Td>{c.vehicle}</Td>
                    <Td><span className={`text-xs font-bold px-2 py-1 rounded-full ${meta.color}`}>{meta.label}</span></Td>
                    <Td>{c.completedServices}</Td>
                    <Td>{c.rating || "—"}</Td>
                    <Td>
                      <button onClick={() => openDetail(c.userId)} data-testid={`review-carrier-${c.userId}`}
                        className="px-3 py-1.5 rounded-full bg-[#0F1115] text-white text-xs font-semibold flex items-center gap-1 hover:bg-[#1A1D24]">
                        <Eye size={14} /> Revisar
                      </button>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pager page={cPage} setPage={setCPage} total={carriers.length} perPage={PER_PAGE} />
        </div>
      )}

      {tab === "freights" && (
        <div className="overflow-x-auto rounded-2xl border border-zinc-200 bg-white" data-testid="freights-table">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-left text-xs uppercase tracking-widest text-zinc-500">
              <tr><Th>Cliente</Th><Th>Origen</Th><Th>Destino</Th><Th>Categoría</Th><Th>Precio</Th><Th>Estado</Th><Th>Transportista</Th></tr>
            </thead>
            <tbody>
              {paginate(freights, fPage).map((f) => {
                const badge = statusBadge(f.status);
                return (
                  <tr key={f.id} className="border-t border-zinc-100" data-testid={`freight-row-${f.id}`}>
                    <Td className="font-medium">{f.client}</Td>
                    <Td>{f.origin}</Td>
                    <Td>{f.destination}</Td>
                    <Td>{f.category}</Td>
                    <Td className="font-semibold">{formatCLP(f.price)}</Td>
                    <Td><span className={`text-xs font-bold px-2 py-1 rounded-full ${badge.color}`}>{badge.label}</span></Td>
                    <Td>{f.carrier}</Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pager page={fPage} setPage={setFPage} total={freights.length} perPage={PER_PAGE} />
        </div>
      )}

      {tab === "messages" && (
        <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="messages-table">
          {messages.length === 0 ? (
            <p className="p-10 text-center text-sm text-zinc-400" data-testid="messages-empty">No hay mensajes de contacto todavía.</p>
          ) : paginate(messages, mPage).map((m) => (
            <div key={m.id} className={`border-b border-zinc-100 p-4 sm:p-5 ${m.read ? "" : "bg-[#FFF4F1]"}`} data-testid={`message-row-${m.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-semibold flex items-center gap-2">
                    {m.name}
                    {!m.read && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FF5A36] text-white" data-testid={`unread-${m.id}`}>Nuevo</span>}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    <a href={`mailto:${m.email}`} className="hover:underline">{m.email}</a>
                    {m.phone ? ` · ${m.phone}` : ""} · {dateTimeLabel(m.createdAt)}
                  </div>
                  {m.subject && <div className="text-sm font-medium mt-2">{m.subject}</div>}
                  <p className="text-sm text-zinc-700 mt-1 whitespace-pre-wrap break-words">{m.message}</p>
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  <a href={`mailto:${m.email}?subject=${encodeURIComponent("Re: " + (m.subject || "Tu consulta en Frets"))}`}
                    className="text-xs font-semibold text-[#FF5A36] hover:underline" data-testid={`reply-msg-${m.id}`}>Responder</a>
                  <button onClick={() => markRead(m.id, !m.read)} data-testid={`toggle-read-${m.id}`}
                    className="text-xs font-semibold text-zinc-500 hover:text-zinc-800">{m.read ? "Marcar no leído" : "Marcar leído"}</button>
                  <button onClick={() => deleteMsg(m.id)} data-testid={`delete-msg-${m.id}`}
                    className="text-xs font-semibold text-red-500 hover:text-red-700 flex items-center gap-1"><Trash size={13} /> Eliminar</button>
                </div>
              </div>
            </div>
          ))}
          <Pager page={mPage} setPage={setMPage} total={messages.length} perPage={PER_PAGE} />
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="carrier-detail-dialog">
          <DialogHeader><DialogTitle>Acreditación del transportista</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto no-scrollbar">
              <div>
                <div className="font-semibold text-lg">{detail.user.name} {detail.user.lastName}</div>
                <div className="text-sm text-zinc-500">{detail.user.email} · {detail.user.phone}</div>
                <div className="text-sm text-zinc-500">{detail.user.city} · RUT {detail.user.rut || "—"}</div>
                <span className={`inline-block mt-2 text-xs font-bold px-2.5 py-1 rounded-full ${ACCREDITATION_META[detail.profile?.accreditationStatus]?.color}`}>
                  {ACCREDITATION_META[detail.profile?.accreditationStatus]?.label}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 border-t border-zinc-100 pt-3" data-testid="carrier-compliance">
                <Field label="RUT empresa" value={detail.profile?.companyRut} />
                <Field label="RNSTC" value={detail.profile?.rnstcNumber} />
                <Field label="Licencia" value={detail.profile?.licenseClass} />
                <Field label="Vence licencia" value={detail.profile?.licenseExpiry} />
                <Field label="Decl. jurada salud" value={detail.profile?.healthDeclaration ? "Sí" : "No"} />
              </div>

              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Vehículos</div>
                {detail.vehicles.length === 0 ? <p className="text-sm text-zinc-400">Sin vehículos</p> : detail.vehicles.map((v) => (
                  <div key={v.id} className="text-sm border border-zinc-200 rounded-xl px-3 py-2 mb-1.5">{v.type} · {v.brand} {v.model} · {v.licensePlate} · {v.capacity}</div>
                ))}
              </div>

              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Documentos</div>
                {detail.documents.length === 0 ? <p className="text-sm text-zinc-400">Sin documentos</p> : detail.documents.map((d) => {
                  const exp = docExp(d);
                  const st = ADMIN_DOC_STATUS[d.status] || ADMIN_DOC_STATUS.PENDIENTE;
                  return (
                    <div key={d.id} className="border border-zinc-200 rounded-xl px-3 py-2.5 mb-1.5" data-testid={`admin-doc-${d.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium">{d.documentType}</span>
                        <div className="flex items-center gap-1.5">
                          {exp && <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${exp.cls}`}>{exp.label}</span>}
                          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                        </div>
                      </div>
                      {d.status === "RECHAZADO" && d.rejectionReason && <p className="text-xs text-red-600 mt-1">Motivo: {d.rejectionReason}</p>}
                      <div className="flex items-center gap-2 mt-2">
                        {d.fileId ? (
                          <a href={`${api.defaults.baseURL}/files/${d.fileId}/download?auth=${localStorage.getItem("flets_token")}`} target="_blank" rel="noreferrer"
                            className="text-[#FF5A36] font-semibold text-xs hover:underline" data-testid={`view-doc-${d.id}`}>Ver documento</a>
                        ) : <span className="text-xs text-zinc-400">Sin archivo</span>}
                        <span className="flex-1" />
                        <button onClick={() => reviewDoc(d.id, "APROBADO")} data-testid={`approve-doc-${d.id}`} className="px-2.5 py-1 rounded-full bg-emerald-600 text-white text-[11px] font-bold hover:bg-emerald-700">Aprobar</button>
                        <button onClick={() => reviewDoc(d.id, "RECHAZADO")} data-testid={`reject-doc-${d.id}`} className="px-2.5 py-1 rounded-full bg-red-600 text-white text-[11px] font-bold hover:bg-red-700">Rechazar</button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-zinc-100">
                <ActionBtn onClick={() => setStatus(detail.user.id, "APROBADO")} disabled={busy} color="bg-emerald-600 hover:bg-emerald-700" testid="approve-carrier"><CheckCircle size={16} weight="bold" /> Aprobar</ActionBtn>
                <ActionBtn onClick={() => setStatus(detail.user.id, "RECHAZADO")} disabled={busy} color="bg-red-600 hover:bg-red-700" testid="reject-carrier"><XCircle size={16} weight="bold" /> Rechazar</ActionBtn>
                <ActionBtn onClick={() => setStatus(detail.user.id, "EN_REVISION")} disabled={busy} color="bg-amber-500 hover:bg-amber-600" testid="review-carrier">En revisión</ActionBtn>
                <ActionBtn onClick={() => setStatus(detail.user.id, "SUSPENDIDO")} disabled={busy} color="bg-zinc-700 hover:bg-zinc-800" testid="suspend-carrier"><PauseCircle size={16} weight="bold" /> Suspender</ActionBtn>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-2xl border p-4 ${accent ? "border-[#FF5A36] bg-[#FFF4F1]" : "border-zinc-200 bg-white"}`}>
      <Icon size={20} weight="bold" className="text-[#FF5A36] mb-2" />
      <div className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{label}</div>
      <div className="font-display text-xl font-black truncate">{value}</div>
    </div>
  );
}
function TabBtn() { return null; }
function Th({ children }) { return <th className="px-4 py-3 font-bold whitespace-nowrap">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 whitespace-nowrap ${className}`}>{children}</td>; }
function Pager({ page, setPage, total, perPage }) {
  const pages = Math.ceil(total / perPage) || 1;
  if (pages <= 1) return null;
  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-100 text-sm" data-testid="pager">
      <span className="text-zinc-500">{from}–{to} de {total}</span>
      <div className="flex items-center gap-1">
        <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} data-testid="pager-prev"
          className="px-3 py-1.5 rounded-lg border border-zinc-300 font-semibold disabled:opacity-40 hover:bg-zinc-50">Anterior</button>
        {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
          <button key={p} onClick={() => setPage(p)} data-testid={`pager-${p}`}
            className={`w-9 h-9 rounded-lg font-semibold ${p === page ? "bg-[#FF5A36] text-white" : "border border-zinc-300 hover:bg-zinc-50"}`}>{p}</button>
        ))}
        <button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages} data-testid="pager-next"
          className="px-3 py-1.5 rounded-lg border border-zinc-300 font-semibold disabled:opacity-40 hover:bg-zinc-50">Siguiente</button>
      </div>
    </div>
  );
}
function ActionBtn({ onClick, disabled, color, children, testid }) {
  return <button onClick={onClick} disabled={disabled} data-testid={testid} className={`py-2.5 rounded-xl text-white text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors active:scale-95 disabled:opacity-60 ${color}`}>{children}</button>;
}
function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{label}</div>
      <div className="text-sm font-medium">{value || "—"}</div>
    </div>
  );
}
const ADMIN_DOC_STATUS = {
  PENDIENTE: { label: "Pendiente", cls: "bg-amber-100 text-amber-700" },
  APROBADO: { label: "Aprobado", cls: "bg-emerald-100 text-emerald-700" },
  RECHAZADO: { label: "Rechazado", cls: "bg-red-100 text-red-700" },
};
function docExp(d) {
  if (!d.expirationDate) return null;
  const days = Math.ceil((new Date(d.expirationDate) - new Date()) / 86400000);
  if (isNaN(days)) return null;
  if (days < 0) return { label: "Vencido", cls: "bg-red-100 text-red-700" };
  if (days <= 15) return { label: `Vence en ${days}d`, cls: "bg-amber-100 text-amber-700" };
  return { label: `Vence ${String(d.expirationDate).slice(0, 10)}`, cls: "bg-zinc-100 text-zinc-600" };
}
